import React, {useEffect, useState} from "react";
import {Box, Button, Paper, Stack, TextField, Tooltip, Typography} from "@mui/material";
import {useAuth} from "../../utils/hooks/useAuth";
import {useConfirm} from "material-ui-confirm";
import {useNavigate, useParams} from "react-router-dom";
import {useNotification} from "../../utils/hooks/useNotification";
import useSWR from "swr";
import {deleteFetcher, getFetcher, putFetcher} from "../../utils/api/fetchers";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {Season, seasonSchema} from "../../schemas/seasons/seasonsSchema";
import UserRole from "../../schemas/user/roles";
import Loading from "../../components/status/Loading";
import ErrorMessage from "../../components/status/ErrorMessage";

const Seasons: React.FC = () => {
    const {hasPermission} = useAuth();
    const confirm = useConfirm();
    const navigate = useNavigate();
    const {seasonId} = useParams<{ seasonId: string }>();
    const {showNotification} = useNotification();
    const {data: season, isLoading, error, mutate} = useSWR<Season>(`/seasons/${seasonId}`, getFetcher);
    const {
        register,
        handleSubmit,
        reset,
        formState: {errors, isDirty},
    } = useForm<Season>({
        defaultValues: season,
        resolver: zodResolver(seasonSchema),
    });

    const [editMode, setEditMode] = useState<boolean>(false);
    const readOnly = !hasPermission(UserRole.COMMODORE);
    const noEdit = readOnly || !editMode;

    useEffect(() => {
        if (season) {
            reset(season);
        }
    }, [season, reset]);

    const onSubmit = async (data: Season) => {
        if (!isDirty) {
            showNotification("Nebyly provedeny žádné změny", "info");
            setEditMode(false);
            return;
        }

        putFetcher(`/seasons/${seasonId}`, data)
            .then(() => {
                showNotification("Sezona byla úspěšně upravena", "success");
                mutate();
                setEditMode(false);
            })
            .catch(() => showNotification("Nepodařilo se upravit sezónu", "error"));
    }

    const handleDelete = () => {
        confirm({
            description: `Opravdu chcete smazat tuto sezónu?`,
            title: "Smazat sezónu",
            confirmationText: "Smazat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        }).then(() => {
            deleteFetcher(`/seasons/${seasonId}`)
                .then(() => {
                    showNotification("Sezona byla úspěšně smazána", "success");
                    navigate("/seasons");
                })
                .catch(() => showNotification("Nepodařilo se smazat sezonu", "error"));
        }).catch(() => {
        });
    }

    if (isLoading || !season) return <Loading/>;
    if (error) return <ErrorMessage/>;

    return (
        <Paper elevation={3} sx={{padding: 4, maxWidth: 600, margin: '0 auto'}}>
            <Typography variant="h4" gutterBottom>
                Detail sezóny
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3}>
                    <Tooltip title="Rok sezóny (nelze změnit)" placement="top" arrow>
                        <TextField
                            fullWidth
                            label="Rok"
                            variant="standard"
                            type="number"
                            required
                            {...register('year', {
                                valueAsNumber: true
                            })}
                            error={!!errors.year}
                            helperText={errors.year?.message}
                            inputProps={{min: 2000, max: 2100}}
                            InputProps={{readOnly: true}}
                        />
                    </Tooltip>
                    <Tooltip title="Počet závodů, které se budou počítat do celkového pořadí pro jednotilvé kategorie"
                             placement="top" arrow>
                        <TextField
                            fullWidth
                            label="Počet započítaných závodů"
                            variant="standard"
                            type="number"
                            required
                            {...register('numberOfCountedRaces', {
                                valueAsNumber: true
                            })}
                            error={!!errors.numberOfCountedRaces}
                            helperText={errors.numberOfCountedRaces?.message}
                            inputProps={{min: 1, max: 100}}
                            InputProps={{readOnly: noEdit}}
                        />
                    </Tooltip>
                    {!readOnly && (
                        editMode ? (
                            <Stack direction='row' spacing={2}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    sx={{width: 250}}
                                    onClick={() => {
                                        setEditMode(false);
                                        reset(season);
                                    }}
                                >
                                    Zrušit změny
                                </Button>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    Uložit
                                </Button>
                            </Stack>
                        ) : (
                            <Stack direction='row' spacing={2}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    sx={{px: 10}}
                                    onClick={handleDelete}
                                >
                                    Smazat
                                </Button>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => setEditMode(true)}
                                >
                                    Upravit
                                </Button>
                            </Stack>
                        )
                    )}
                </Stack>
            </Box>
        </Paper>
    );
}

export default Seasons;